import * as d3 from 'd3';
import React from 'react';

import Axis from 'src/components/D3Axis';
import {LegendConfig, makeLine, makeRect} from 'src/components/Legend';
import {MARGIN} from 'src/components/Viz';
import WithWidth from 'src/components/WithWidth';
import {PrecipToDate} from 'src/lib/api/regionalReport';
import COLORS from 'src/lib/colors';
import {Dims} from 'src/lib/commonTypes';
import {getGraphDims} from 'src/lib/vizUtils';

import {StaticLegend} from './ForecastEvolutionChart';

const CHART_HEIGHT = 260;
const CHART_MARGIN = {...MARGIN, bottom: 30, top: 12, right: 20};
const CURRENT_COLOR = COLORS.SWANN_OBS;
const RANGE_COLOR = COLORS.AXIS_LIGHT;
const MEAN_COLOR = '#111827';
const MEDIAN_COLOR = COLORS.AXIS;
const DAY_MS = 24 * 3600 * 1000;

type Point = {date: Date; value: number};

function dateAt(data: PrecipToDate, i: number): Date {
  return new Date(new Date(data.start).getTime() + data.daysSinceStart[i] * DAY_MS);
}

function series(data: PrecipToDate, values: (number | null)[] | undefined): Point[] {
  return (values ?? []).flatMap((value, i) =>
    value == null ? [] : [{date: dateAt(data, i), value}]
  );
}

function Chart({data, width}: {data: PrecipToDate; width: number}) {
  const dims: Dims = [width, CHART_HEIGHT];
  const [graphW, graphH] = getGraphDims(dims, CHART_MARGIN);
  const start = new Date(data.start);
  const cutoff = new Date(data.cutoff);

  const current = series(data, data.current);
  const mean = series(data, data.history?.mean);
  const median = series(data, data.history?.median);
  const p90 = series(data, data.history?.p90);
  const band = (data.history?.p10 ?? []).flatMap((low, i) => {
    const high = data.history?.p90[i];
    return low == null || high == null ? [] : [{date: dateAt(data, i), low, high}];
  });

  const yMax = d3.max([...current, ...mean, ...p90], (p) => p.value) || 1;
  const xScale = d3.scaleUtc().domain([start, cutoff]).range([0, graphW]);
  const yScale = d3
    .scaleLinear()
    .domain([0, yMax * 1.05])
    .range([graphH, 0])
    .nice();

  const line = d3
    .line<Point>()
    .x((p) => xScale(p.date))
    .y((p) => yScale(p.value));
  const area = d3
    .area<{date: Date; low: number; high: number}>()
    .x((p) => xScale(p.date))
    .y0((p) => yScale(p.low))
    .y1((p) => yScale(p.high));

  return (
    <svg width={dims[0]} height={dims[1]}>
      <g transform={`translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`}>
        <path d={area(band) || ''} fill={RANGE_COLOR} opacity={0.8} />
        <path
          d={line(mean) || ''}
          fill="none"
          stroke={MEAN_COLOR}
          strokeWidth={1.3}
          strokeDasharray="6,3"
        />
        <path
          d={line(median) || ''}
          fill="none"
          stroke={MEDIAN_COLOR}
          strokeWidth={1.3}
          strokeDasharray="2,2"
        />
        <path d={line(current) || ''} fill="none" stroke={CURRENT_COLOR} strokeWidth={2.5} />
        <Axis
          axisCreator={d3
            .axisBottom(xScale)
            .ticks(Math.max(2, Math.floor(graphW / 80)))
            .tickFormat((d) => d3.utcFormat('%b %-d')(d as Date))
            .tickSizeOuter(0)}
          transform={`translate(0,${graphH})`}
        />
        <Axis
          axisCreator={d3.axisLeft(yScale).ticks(5)}
          yLabelProps={{graphDims: [graphW, graphH], text: `Precipitation (${data.unit})`}}
        />
      </g>
    </svg>
  );
}

export default function PrecipToDateChart({data}: {data: PrecipToDate}) {
  const years = data.historyWaterYears;
  const span = years.length ? `Water years ${years[0]}–${years[years.length - 1]}` : '';
  const legendConfigs: LegendConfig[] = [
    {
      id: 'current',
      display: `Water year ${data.waterYear}`,
      makeIcon: () => makeLine({stroke: CURRENT_COLOR, strokeWidth: 2.5}),
    },
  ];
  if (data.history) {
    legendConfigs.push(
      {
        id: 'mean',
        display: `${span}, average`,
        makeIcon: () => makeLine({stroke: MEAN_COLOR, strokeWidth: 1.5, strokeDasharray: '6 3'}),
      },
      {
        id: 'median',
        display: `${span}, median`,
        makeIcon: () => makeLine({stroke: MEDIAN_COLOR, strokeWidth: 1.5, strokeDasharray: '2 2'}),
      },
      {
        id: 'range',
        display: '10th–90th percentile of those years',
        makeIcon: () => makeRect({fill: RANGE_COLOR}),
      }
    );
  }
  return (
    <div>
      <WithWidth>{(width) => <Chart data={data} width={width} />}</WithWidth>
      <StaticLegend configs={legendConfigs} />
    </div>
  );
}
